import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';

const ProductsPage = () => {
  const [searchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category');

  const products = [
    {
      id: 'conveyor-belts',
      name: 'Conveyor Belts',
      description: 'Heavy-duty material handling solutions with cleated, rough top & food-grade options for industrial applications.',
      image: 'https://5.imimg.com/data5/SELLER/Default/2023/8/331023420/ZW/MD/SU/1393287/flat-belting-industrial-belting-500x500.jpg',
      features: [
        'Heavy-duty construction',
        'Cleated and rough top options',
        'Food-grade variants available',
        'Oil & chemical resistant',
        'Temperature resistant up to 150°C'
      ],
      applications: ['Mining', 'Food Processing', 'Packaging', 'Manufacturing', 'Logistics'],
      specs: {
        width: '50mm - 2000mm',
        thickness: '3mm - 15mm',
        temperature: '-20°C to 150°C'
      }
    },
    {
      id: 'heavy-duty-v-belts',
      name: 'Heavy Duty V-Belts',
      description: 'Banded, perforated & classical V-belts for power transmission in demanding industrial environments.',
      image: 'https://5.imimg.com/data5/SELLER/Default/2023/12/366802659/VT/BX/IJ/1393287/banded-v-belts-500x500.jpg',
      features: [
        'High power transmission efficiency',
        'Banded construction for stability',
        'Anti-static options available',
        'Long service life',
        'Low maintenance requirements'
      ],
      applications: ['Compressors', 'Pumps', 'Generators', 'HVAC Systems', 'Agricultural Equipment'],
      specs: {
        sections: 'A, B, C, D, E',
        length: 'Custom lengths available',
        power: 'Up to 500 HP'
      }
    },
    {
      id: 'caterpillar-belts',
      name: 'Caterpillar Belts',
      description: 'Industrial haul-off & rubber caterpillar belts for specialized pulling and gripping applications.',
      image: 'https://5.imimg.com/data5/IOS/Default/2022/9/RQ/RR/US/1393287/product-jpeg-500x500.png',
      features: [
        'High grip surface texture',
        'Excellent wear resistance',
        'Custom lengths available',
        'Reinforced construction',
        'Suitable for continuous operation'
      ],
      applications: ['Pipe Extrusion', 'Cable Manufacturing', 'Profile Extrusion', 'Tube Production'],
      specs: {
        width: '50mm - 500mm',
        thickness: '10mm - 30mm',
        grip: 'High friction coefficient'
      }
    },
    {
      id: 'take-up-drive-belts',
      name: 'Take Up Drive Belts',
      description: 'Top T Bottom V design for precision drive applications with optimal power transmission.',
      image: 'https://5.imimg.com/data5/IOS/Default/2022/9/BA/ZG/ID/1393287/product-jpeg-500x500.png',
      features: [
        'Precise tension control',
        'Low maintenance design',
        'Extended service life',
        'Quiet operation',
        'High efficiency transmission'
      ],
      applications: ['Textile Machinery', 'Printing Equipment', 'Packaging Machines', 'Conveyor Systems'],
      specs: {
        profile: 'Top T / Bottom V',
        width: '25mm - 200mm',
        speed: 'Up to 30 m/s'
      }
    },
    {
      id: 'wedge-belts',
      name: 'Wedge Belts',
      description: 'Variable speed & hexagonal V-belts for efficient power transmission in industrial machinery.',
      image: 'https://5.imimg.com/data5/SELLER/Default/2023/12/366796399/PA/NM/CX/1393287/variable-speed-belt-250x250.jpg',
      features: [
        'Wide speed range capability',
        'Low vibration operation',
        'High efficiency design',
        'Compact cross-section',
        'Superior flexibility'
      ],
      applications: ['Variable Speed Drives', 'Machine Tools', 'Industrial Fans', 'Automotive'],
      specs: {
        sections: '3V, 5V, 8V',
        ratio: 'Up to 10:1',
        efficiency: '97%+'
      }
    },
    {
      id: 'food-grade-belts',
      name: 'Food Grade Belts',
      description: 'FDA & EU compliant hygienic belts for food processing industries with anti-microbial properties.',
      image: 'https://5.imimg.com/data5/SELLER/Default/2025/7/526497469/QY/CW/OH/1393287/21oqb1hfqal-uf1000-1000-ql80-500x500.jpg',
      features: [
        'FDA approved materials',
        'EU 10/2011 compliant',
        'Anti-microbial surface',
        'Easy to clean',
        'Chemical resistant'
      ],
      applications: ['Bakery', 'Meat Processing', 'Dairy', 'Confectionery', 'Beverage'],
      specs: {
        certification: 'FDA, EU 10/2011',
        temperature: '-30°C to 100°C',
        surface: 'Non-porous, hygienic'
      }
    }
  ];

  const filteredProducts = selectedCategory 
    ? products.filter(p => p.id === selectedCategory)
    : products;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E293B] pt-40 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-bold text-4xl sm:text-5xl text-white mb-4">
            {selectedCategory ? filteredProducts[0]?.name : 'Our Products'}
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            {selectedCategory 
              ? filteredProducts[0]?.description 
              : 'Comprehensive range of power transmission belts and industrial belting solutions'}
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {!selectedCategory && (
            <div className="flex flex-wrap gap-3 mb-10 justify-center">
              <Link to="/products">
                <Button variant={!selectedCategory ? "default" : "outline"} className="rounded-full">
                  All Products
                </Button>
              </Link>
              {products.map(product => (
                <Link key={product.id} to={`/products?category=${product.id}`}>
                  <Button variant="outline" className="rounded-full">
                    {product.name}
                  </Button>
                </Link>
              ))}
            </div>
          )}

          <div className="space-y-16">
            {filteredProducts.map((product, index) => (
              <div 
                key={product.id} 
                id={product.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-r from-[#2563EB]/20 to-[#60A5FA]/20 rounded-2xl blur-xl"></div>
                    <img
                      src={product.image}
                      alt={product.name}
                      className="relative w-full h-[350px] object-cover rounded-xl shadow-xl"
                    />
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <h2 className="font-bold text-3xl text-[#1E293B] mb-4">{product.name}</h2>
                  <p className="text-[#64748B] text-lg mb-6">{product.description}</p>
                  
                  <div className="mb-6">
                    <h3 className="font-semibold text-[#1E293B] mb-3">Key Features:</h3>
                    <ul className="space-y-2">
                      {product.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-[#475569]">
                          <CheckCircle className="text-[#2563EB] mr-2 flex-shrink-0" size={18} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h3 className="font-semibold text-[#1E293B] mb-3">Applications:</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.applications.map((app, i) => (
                        <span key={i} className="px-3 py-1 bg-[#EFF6FF] text-[#2563EB] text-sm rounded-full">
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
                    <h3 className="font-semibold text-[#1E293B] mb-3">Specifications:</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {Object.entries(product.specs).map(([key, value]) => (
                        <div key={key}>
                          <span className="text-[#64748B] capitalize">{key}:</span>
                          <span className="text-[#1E293B] ml-2 font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    asChild
                    size="lg"
                    className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:from-[#1D4ED8] hover:to-[#2563EB] text-white rounded-lg"
                  >
                    <Link to={`/configure?belt_type=${encodeURIComponent(product.name)}`}>
                      Request Quotation <ArrowRight className="ml-2" size={18} />
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
